// apps/webapp/src/platform/monitored-places.ts
import { cellToLatLng, cellToParent, getResolution } from "h3-js";
import { cellsWithinRadius, radiusDisplayRes } from "../map/home-radius.js";
import {
  W_THRESH, cellDistanceKm, inFootprint, type IncidentView, type StatusLevel,
} from "../screens/now/incident-model.js";

/** A watch ring. `live` rings drive only the in-app union (never server push). */
export interface MonitoredRing {
  kind: "home" | "saved" | "live";
  cell: string;
  radiusKm: number;
  placeName?: string;
}

/** An in-radius incident, tagged with the nearest ring that matched it. */
export interface InRadiusEntry { view: IncidentView; distanceKm: number; ring: MonitoredRing; }

/** An incident's relationship to the watch ring it is most "inside of". */
export interface ZoneRelation { distanceKm: number; radiusKm: number; placeName?: string; }

/** The watch ring an incident is most inside of — the one minimising distance / radius —
 *  across ALL monitored zones (home + saved + live), not just home. IncidentDetail uses it
 *  to judge "near" (within that ring's radius) vs "away", and whether to keep safety steps
 *  (within 2× its radius), so a fire far from home but inside a saved zone still reads as
 *  near. Returns undefined when there is no usable ring or the incident has no cell. */
export function zoneRelation(rings: MonitoredRing[], incidentCell: string): ZoneRelation | undefined {
  if (!incidentCell) return undefined;
  let best: { rel: ZoneRelation; ratio: number } | undefined;
  for (const ring of rings) {
    if (!ring.cell || ring.radiusKm <= 0) continue;
    const distanceKm = cellDistanceKm(ring.cell, incidentCell);
    const ratio = distanceKm / ring.radiusKm;
    if (!best || ratio < best.ratio) {
      best = { rel: { distanceKm, radiusKm: ring.radiusKm, placeName: ring.placeName }, ratio };
    }
  }
  return best?.rel;
}

/** Build the monitored-ring set from session state, FORWARDING each zone's `placeName`
 *  (the home + saved rings carry it) so the Now "Other watched places" list and the
 *  watched-zone toasts can name the zone. Saved rings keep `session.places` array order
 *  (the canonical zone order). The optional `live` ring is appended last. */
export function buildMonitoredRings(
  home: { cell: string; radiusKm: number; placeName?: string } | undefined,
  places: Array<{ cell: string; radiusKm: number; placeName?: string }>,
  live: MonitoredRing | null,
): MonitoredRing[] {
  const rings: MonitoredRing[] = [];
  if (home?.cell) rings.push({ kind: "home", cell: home.cell, radiusKm: home.radiusKm, placeName: home.placeName });
  for (const p of places) rings.push({ kind: "saved", cell: p.cell, radiusKm: p.radiusKm, placeName: p.placeName });
  if (live) rings.push(live);
  return rings;
}

/** Union of homeStatus across all rings: an incident in ANY ring counts once, at its
 *  nearest ring distance. Classifies by the same W threshold homeStatus uses. */
export function placesStatus(
  views: IncidentView[],
  rings: MonitoredRing[],
  _nowMicros: number,
): { level: StatusLevel; nearest?: IncidentView; inRadius: InRadiusEntry[] } {
  // Build each ring's watch set once (same cost homeStatus pays, amortised over all views).
  const caches = rings
    .filter((r) => r.cell)
    .map((ring) => {
      const [hlat, hlng] = cellToLatLng(ring.cell);
      return { ring, res: radiusDisplayRes(ring.radiusKm), watch: new Set(cellsWithinRadius(hlat, hlng, ring.radiusKm)) };
    });

  const bySubject = new Map<string, InRadiusEntry>();
  for (const view of views) {
    if (!view.cell) continue;
    let best: number | null = null;
    let bestRing: MonitoredRing | null = null;
    for (const { ring, res, watch } of caches) {
      const inW = (getResolution(view.cell) >= res
        ? watch.has(cellToParent(view.cell, res))
        : cellDistanceKm(ring.cell, view.cell) <= ring.radiusKm)
        || inFootprint(ring.cell, view.footprint);
      if (!inW) continue;
      const d = cellDistanceKm(ring.cell, view.cell);
      if (best === null || d < best) { best = d; bestRing = ring; }
    }
    if (best === null || !bestRing) continue;
    const prev = bySubject.get(view.subject);
    if (!prev || best < prev.distanceKm) bySubject.set(view.subject, { view, distanceKm: best, ring: bestRing });
  }
  const inRadius = [...bySubject.values()].sort((a, b) => a.distanceKm - b.distanceKm);
  const confirmed = inRadius.find((x) => x.view.W >= W_THRESH);
  if (confirmed) return { level: "nearby", nearest: confirmed.view, inRadius };
  if (inRadius.length > 0) return { level: "stayAware", nearest: inRadius[0].view, inRadius };
  return { level: "allClear", inRadius };
}

/** Cells to register for server push: union of home + saved watch cells. Live excluded. */
export function subscriptionCells(rings: MonitoredRing[]): string[] {
  const out = new Set<string>();
  for (const ring of rings) {
    if (ring.kind === "live" || !ring.cell) continue;
    const [lat, lng] = cellToLatLng(ring.cell);
    for (const c of cellsWithinRadius(lat, lng, ring.radiusKm)) out.add(c);
  }
  return [...out];
}

/** Secondary-zone (saved) incidents for the Now "Other watched places" list: the
 *  union over `secondaryRings`, minus any subject already attributed to a primary
 *  zone (`exclude`), sorted by ring order (the secondaryRings array order) then
 *  distance. Each entry carries the nearest secondary `ring` for its zone name. */
export function watchedIncidents(
  views: IncidentView[],
  secondaryRings: MonitoredRing[],
  exclude: Set<string>,
): InRadiusEntry[] {
  const order = new Map(secondaryRings.map((r, i) => [`${r.cell}:${r.radiusKm}`, i]));
  const rank = (e: InRadiusEntry) => order.get(`${e.ring.cell}:${e.ring.radiusKm}`) ?? Number.MAX_SAFE_INTEGER;
  return placesStatus(views, secondaryRings, 0).inRadius
    .filter((e) => !exclude.has(e.view.subject))
    .sort((a, b) => (rank(a) - rank(b)) || (a.distanceKm - b.distanceKm));
}
